"use client";

import type React from "react";
import { useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  VStack,
  Heading,
  Text,
  Alert,
  AlertIcon,
  Link as ChakraLink,
  Container,
  Card,
  CardBody,
  useToast,
  Image,
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import { supabase } from "../supabaseClient";
import { useNavigate, Link } from "react-router-dom";

export default function SignUp() {
  const [loading, setLoading] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [verificationSent, setVerificationSent] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();

  // Function to check if profile exists and create it via server if needed
  const ensureProfileExists = async (userId: string, userData: any) => {
    try {
      // First check if profile already exists
      const { data: existingProfile } = await supabase
        .from("profiles")
        .select("id")
        .eq("id", userId)
        .maybeSingle();

      if (existingProfile) {
        return true;
      }

      // If no profile exists, try to create it via the server endpoint
      // This uses the service role key which bypasses RLS
      if (process.env.REACT_APP_SERVER_URL) {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_SERVER_URL}/create-profile`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                userId,
                email: userData.email,
                fullName: userData.full_name,
                username: userData.username,
              }),
            }
          );

          if (response.ok) {
            return true;
          } else {
            console.error(
              "Server profile creation failed:",
              await response.text()
            );
          }
        } catch (serverError) {
          console.error("Server profile creation error:", serverError);
        }
      }

      // Fallback: Wait a bit for the trigger to work, then check again
      await new Promise((resolve) => setTimeout(resolve, 2000));

      const { data: profileAfterWait } = await supabase
        .from("profiles")
        .select("id")
        .eq("id", userId)
        .maybeSingle();

      if (profileAfterWait) {
        return true;
      }

      console.error("Profile creation failed for user:", userId);
      return false;
    } catch (err) {
      console.error("Unexpected error ensuring profile exists:", err);
      return false;
    }
  };

  const handleSignUp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (password !== confirmPassword) {
      setError("Passwords don't match");
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters long");
      setLoading(false);
      return;
    }

    try {
      const userData = {
        first_name: firstName,
        last_name: lastName,
        full_name: `${firstName} ${lastName}`.trim(),
        username: email.split("@")[0],
        email: email,
      };

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: userData,
        },
      });

      if (error) {
        console.error("Signup error:", error);
        setError(
          error.message || "An error occurred during signup. Please try again."
        );
        return;
      }

      if (data.user) {
        if (!data.user.email_confirmed_at) {
          setVerificationSent(true);
          navigate("/verify", { state: { email } });
        } else if (data.user.email_confirmed_at) {
          navigate("/dashboard");
        }
      }
    } catch (error: any) {
      console.error("Unexpected signup error:", error);
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (verificationSent) {
    return (
      <Container maxW="md" py={12}>
        <Card>
          <CardBody p={8}>
            <VStack spacing={6} textAlign="center">
              <Image
                src="/main-logo.png"
                alt="RadiusDocs AI Logo"
                maxH="200px"
                objectFit="contain"
              />
              <Heading size="lg" color="gray.700">
                Verification Email Sent
              </Heading>
              <Text color="gray.600">
                Please check your email ({email}) for a verification link to
                complete your registration.
              </Text>
              <Text fontSize="sm" color="gray.600">
                Already have an account?{" "}
                <ChakraLink as={Link} to="/login" color="primary.600">
                  Sign In
                </ChakraLink>
              </Text>
            </VStack>
          </CardBody>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxW="md" py={12}>
      <Card>
        <CardBody p={8}>
          <VStack spacing={6}>
            <VStack spacing={4} textAlign="center">
              <Image
                src="/main-logo.png"
                alt="RadiusDocs AI Logo"
                maxH="200px"
                objectFit="contain"
              />
              <Heading size="lg" color="gray.700">
                Create an account
              </Heading>
              <Text color="gray.600">
                Get started with RadiusDocs today. No credit card required to
                Sign Up.
              </Text>
            </VStack>

            {error && (
              <Alert status="error" borderRadius="md" textColor={"red.600"}>
                <AlertIcon />
                {error}
              </Alert>
            )}

            <Box as="form" onSubmit={handleSignUp} w="100%">
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel color="gray.600">First Name</FormLabel>
                  <Input
                    type="text"
                    placeholder="Your first name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    isDisabled={loading}
                    bg="gray.50"
                    _placeholder={{ color: "gray.500" }}
                    textFillColor={"gray.600"}
                    sx={{ caretColor: "black" }}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel color="gray.600">Last Name</FormLabel>
                  <Input
                    type="text"
                    placeholder="Your last name"
                    value={lastName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setLastName(e.target.value)
                    }
                    isDisabled={loading}
                    bg="gray.50"
                    _placeholder={{ color: "gray.500" }}
                    textFillColor={"gray.600"}
                    sx={{ caretColor: "black" }}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel color="gray.600">Email</FormLabel>
                  <Input
                    type="email"
                    placeholder="Your email"
                    value={email}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setEmail(e.target.value)
                    }
                    isDisabled={loading}
                    bg="gray.50"
                    _placeholder={{ color: "gray.500" }}
                    textFillColor={"gray.600"}
                    sx={{ caretColor: "black" }}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel color="gray.600">Password</FormLabel>
                  <InputGroup>
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder="Your password (min 6 characters)"
                      value={password}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setPassword(e.target.value)
                      }
                      isDisabled={loading}
                      minLength={6}
                      bg="gray.50"
                      _placeholder={{ color: "gray.500" }}
                      textFillColor={"gray.600"}
                      sx={{ caretColor: "black" }}
                    />
                    <InputRightElement>
                      <IconButton
                        aria-label={
                          showPassword ? "Hide password" : "Show password"
                        }
                        icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                        onClick={() => setShowPassword(!showPassword)}
                        isDisabled={loading}
                        variant="ghost"
                        size="sm"
                        _placeholder={{ color: "gray.500" }}
                        textFillColor={"gray.600"}
                        sx={{ caretColor: "black" }}
                        color={"primary.500"}
                      />
                    </InputRightElement>
                  </InputGroup>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel color="gray.600">Confirm Password</FormLabel>
                  <InputGroup>
                    <Input
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Confirm your password"
                      value={confirmPassword}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setConfirmPassword(e.target.value)
                      }
                      isDisabled={loading}
                      minLength={6}
                      bg="gray.50"
                      _placeholder={{ color: "gray.500" }}
                      textFillColor={"gray.600"}
                      sx={{ caretColor: "black" }}
                    />
                    <InputRightElement>
                      <IconButton
                        aria-label={
                          showConfirmPassword
                            ? "Hide password"
                            : "Show password"
                        }
                        icon={
                          showConfirmPassword ? <ViewOffIcon /> : <ViewIcon />
                        }
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        isDisabled={loading}
                        variant="ghost"
                        size="sm"
                        color={"primary.500"}
                      />
                    </InputRightElement>
                  </InputGroup>
                </FormControl>

                <Button
                  type="submit"
                  colorScheme="primary"
                  size="lg"
                  w="100%"
                  isLoading={loading}
                  loadingText="Creating Account..."
                >
                  Create Account
                </Button>
              </VStack>
            </Box>

            <Text fontSize="sm" color="gray.600">
              Already have an account?{" "}
              <ChakraLink as={Link} to="/login" color="primary.600">
                Sign In
              </ChakraLink>
            </Text>
          </VStack>
        </CardBody>
      </Card>
    </Container>
  );
}
